import { createFileRoute, useSearch } from "@tanstack/react-router";
import { z } from "zod";
import { ContactForm } from "@/components/contact/ContactForm";
import { ContactInfo } from "@/components/contact/ContactInfo";
import { FAQSection } from "@/components/contact/faq-section";
import { faqQueryOptions, siteSettingsQueryOptions } from "@/lib/query-options";

/**
 * URL search params schema for pre-filling form fields
 * Supports ?service= parameter from Services page CTAs
 */
const searchParamsSchema = z.object({
	service: z.string().optional(),
});

/**
 * Map URL service parameter to project type values
 */
function mapServiceToProjectType(
	service: string | undefined,
): string | undefined {
	if (!service) {
		return;
	}

	const serviceMap: Record<string, string> = {
		"full-design": "residential",
		renovation: "commercial",
		consultation: "consultation",
		hospitality: "hospitality",
	};

	return serviceMap[service.toLowerCase()];
}

export const Route = createFileRoute("/contact")({
	component: ContactPage,
	validateSearch: searchParamsSchema,
	loader: async ({ context }) => {
		/**
		 * SSR: Prefetch contact page data for instant hydration
		 * - Site settings (contact info) for ContactInfo component
		 * - FAQ items for FAQSection component
		 */
		const { queryClient } = context;
		await Promise.allSettled([
			queryClient.ensureQueryData(siteSettingsQueryOptions("contact")),
			queryClient.ensureQueryData(faqQueryOptions()),
		]);
	},
	head: () => ({
		meta: [
			{ title: "Contact Maryam Ahmed | Interior Design Inquiry" },
			{
				name: "description",
				content:
					"Contact Maryam Ahmed for interior design consultations in Bahrain. Request a quote for residential, commercial, or hospitality design projects.",
			},
			{ property: "og:title", content: "Contact | Designs by Maryam" },
			{
				property: "og:description",
				content:
					"Get in touch with Maryam Ahmed for your next interior design project in Bahrain.",
			},
			{ property: "og:type", content: "website" },
			{ property: "og:image", content: "/og-contact.jpg" },
		],
	}),
});

function ContactPage() {
	const { service } = useSearch({ from: "/contact" });
	const defaultProjectType = mapServiceToProjectType(service);

	return (
		<div className="min-h-screen">
			{/* Hero Section */}
			<section className="relative border-b bg-gradient-to-br from-background via-muted/5 to-background py-20 md:py-32">
				{/* Subtle grid pattern overlay */}
				<div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(198_15%_85%_/_0.3)_1px,transparent_1px),linear-gradient(to_bottom,hsl(198_15%_85%_/_0.3)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] dark:bg-[linear-gradient(to_right,hsl(198_15%_25%_/_0.3)_1px,transparent_1px),linear-gradient(to_bottom,hsl(198_15%_25%_/_0.3)_1px,transparent_1px)]" />
				<div className="container relative mx-auto px-4">
					<div className="mx-auto max-w-3xl text-center">
						<h1 className="mb-4 font-bold text-4xl tracking-tight md:text-5xl">
							Let's Create Something Beautiful
						</h1>
						<p className="text-lg text-muted-foreground md:text-xl">
							Ready to transform your space? Fill out the form below and we'll
							get back to you within 24 hours to discuss your project.
						</p>
					</div>
				</div>
			</section>

			{/* Main Content Section */}
			<section className="py-16 md:py-24">
				<div className="container mx-auto px-4">
					<div className="grid gap-12 lg:grid-cols-3">
						{/* Contact Form - Takes 2 columns on large screens */}
						<div className="lg:col-span-2">
							<div className="mb-8">
								<h2 className="mb-2 font-bold text-3xl tracking-tight md:text-4xl">
									Send Us a Message
								</h2>
								<p className="text-muted-foreground">
									Tell us about your project and we'll be in touch to discuss
									how we can help.
								</p>
							</div>
							<ContactForm defaultProjectType={defaultProjectType} />
						</div>

						{/* Contact Info Sidebar */}
						<div className="lg:col-span-1">
							<ContactInfo />
						</div>
					</div>
				</div>
			</section>

			{/* FAQ Section */}
			<section className="border-t bg-muted/30 py-12 md:py-16">
				<div className="container mx-auto px-4">
					<div className="mx-auto max-w-3xl">
						<FAQSection />
					</div>
				</div>
			</section>
		</div>
	);
}
